import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import ActionsMenu from '../../components/common/ActionsMenu';
import Modal from '../../components/common/Modal';
import { ADMIN_ALL_USERS_QUERY, ADMIN_ALL_USER_PLANS_QUERY } from '../../graphql/queries';
import { ADMIN_SET_USER_ROLE_MUTATION, ADMIN_TOGGLE_USER_ACTIVE_MUTATION, ADMIN_UPDATE_USER_PERMISSIONS_MUTATION, ADMIN_ASSIGN_USER_PLAN_MUTATION, ADMIN_RESET_USER_PASSWORD_MUTATION, ADMIN_RECALCULATE_STORAGE_MUTATION } from '../../graphql/mutations';
import { useAuth } from '../../contexts/AuthContext';
import MultiSelectDropdown from '../../components/common/MultiSelectDropdown';
import { ADMIN_MODULES, PlatformRole, isStaff, roleLabel } from '../../utils/staff';
import StorageRecalcPanel from './StorageRecalcPanel';
import styles from './AdminUsers.module.css';
import { PERMISSIONS_META } from '../../constants/permissions';

const ROLES: { value: PlatformRole; description: string }[] = [
  { value: 'superadmin', description: 'Everything, including every admin page and choosing who is an admin' },
  { value: 'admin', description: 'Only the admin pages picked below' },
  { value: 'user', description: 'A customer account: no admin pages' },
];

const MODULE_OPTIONS = ADMIN_MODULES.map((m) => ({ value: m.key, label: m.label }));

const SUBUSER_STATUS: Record<string, string> = { invited: 'Invited', active: 'Active', deactivated: 'Deactivated', removed: 'Removed' };

const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const ROLE_COLORS: Record<string, string> = {
  superadmin: 'text-[10px] font-bold text-purple-600 bg-purple-50 px-2 py-1 rounded-full',
  admin: 'text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded-full',
  user: 'badge-green',
};

interface AdminUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  role: string;
  adminModules: string[];
  isSubuser: boolean;
  subuserStatus: string | null;
  subuserCount: number;
  parentUser: { id: string; email: string; firstName: string; lastName: string } | null;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  permissions: { sceneVisible: boolean; lgsxUpload: boolean; matterportVisible: boolean; releaseNotesVisible: boolean; gaussianVisible: boolean; gaussianGenerate: boolean; orthomosaicVisible: boolean; video360Visible: boolean; basemapVisible: boolean };
  userPlan?: { id: string; name: string } | null;
}

interface UserPlanOption {
  id: string;
  name: string;
}

const AdminUsers = () => {
  const navigate = useNavigate();
  const { user: currentUser, isSuperAdmin } = useAuth();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [parentFilter, setParentFilter] = useState<AdminUser | null>(null);
  const [roleTarget, setRoleTarget] = useState<AdminUser | null>(null);
  const [selectedRole, setSelectedRole] = useState<PlatformRole>('user');
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [permTarget, setPermTarget] = useState<AdminUser | null>(null);
  const [draftPerms, setDraftPerms] = useState<AdminUser['permissions']>({ sceneVisible: false, lgsxUpload: false, matterportVisible: false, releaseNotesVisible: false, gaussianVisible: false, gaussianGenerate: false, orthomosaicVisible: false, video360Visible: false, basemapVisible: false });
  const [planTarget, setPlanTarget] = useState<AdminUser | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<AdminUser | null>(null);
  const limit = 20;

  const { data, loading, error, refetch } = useQuery(ADMIN_ALL_USERS_QUERY, {
    variables: {
      page, limit, search: search || undefined,
      role: roleFilter || undefined,
      account: parentFilter ? undefined : accountFilter || undefined,
      parentUserId: parentFilter?.id,
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: plansData } = useQuery(ADMIN_ALL_USER_PLANS_QUERY, { fetchPolicy: 'cache-first' });
  const userPlanOptions: UserPlanOption[] = plansData?.adminAllUserPlans ?? [];

  const ignoreError = () => {};

  const [setUserRole, { loading: savingRole, error: roleError, reset: resetRoleMutation }] = useMutation(ADMIN_SET_USER_ROLE_MUTATION, {
    onCompleted: () => { setRoleTarget(null); refetch(); },
    onError: ignoreError,
  });

  const [toggleUserActive, { loading: toggling, error: toggleError, reset: resetToggleMutation }] = useMutation(ADMIN_TOGGLE_USER_ACTIVE_MUTATION, {
    onCompleted: () => { setDeactivateTarget(null); refetch(); },
    onError: ignoreError,
  });

  const [updatePermissions, { loading: savingPerms, error: permsError, reset: resetPermsMutation }] = useMutation(ADMIN_UPDATE_USER_PERMISSIONS_MUTATION, {
    onCompleted: () => { setPermTarget(null); refetch(); },
    onError: ignoreError,
  });

  const [assignUserPlan, { loading: savingPlan, error: planError, reset: resetPlanMutation }] = useMutation(ADMIN_ASSIGN_USER_PLAN_MUTATION, {
    onCompleted: () => { setPlanTarget(null); refetch(); },
    onError: ignoreError,
  });

  const [resetUserPassword, { loading: resetting, error: resetError, reset: resetPasswordMutation }] = useMutation(ADMIN_RESET_USER_PASSWORD_MUTATION, {
    onCompleted: () => {
      setResetSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: ignoreError,
  });

  // Reads real folder sizes from S3 into the storage records; results show in StorageRecalcPanel.
  const [recalculateStorage, { loading: recalcStarting, error: recalcError }] = useMutation(ADMIN_RECALCULATE_STORAGE_MUTATION, {
    refetchQueries: ['AdminStorageRecalcs'],
    onError: ignoreError,
  });

  const users: AdminUser[] = data?.adminAllUsers?.users || [];
  const total = data?.adminAllUsers?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const handleSearch = (query: string) => {
    setSearch(query);
    setPage(1);
  };

  const handleOpenRoleModal = (user: AdminUser) => {
    resetRoleMutation();
    setSelectedRole(ROLES.some((r) => r.value === user.role) ? user.role as PlatformRole : 'user');
    setSelectedModules(user.adminModules ?? []);
    setRoleTarget(user);
  };

  // Staff accounts are a super admin's business (the backend refuses the rest).
  const mayManage = (user: AdminUser) => user.id !== currentUser?.id && (isSuperAdmin || !isStaff(user));

  const handleToggleActive = (user: AdminUser) => {
    resetToggleMutation();
    if (user.isActive) {
      setDeactivateTarget(user);
      return;
    }
    toggleUserActive({ variables: { userId: user.id } });
  };

  const handleOpenPermModal = (user: AdminUser) => {
    resetPermsMutation();
    setDraftPerms({
      sceneVisible: user.permissions.sceneVisible ?? false,
      lgsxUpload: user.permissions.lgsxUpload ?? false,
      matterportVisible: user.permissions.matterportVisible ?? false,
      releaseNotesVisible: user.permissions.releaseNotesVisible ?? false,
      gaussianVisible: user.permissions.gaussianVisible ?? false,
      gaussianGenerate: user.permissions.gaussianGenerate ?? false,
      orthomosaicVisible: user.permissions.orthomosaicVisible ?? false,
      video360Visible: user.permissions.video360Visible ?? false,
      basemapVisible: user.permissions.basemapVisible ?? false,
    });
    setPermTarget(user);
  };

  const handleOpenPlanModal = (user: AdminUser) => {
    resetPlanMutation();
    setSelectedPlanId(user.userPlan?.id ?? '');
    setPlanTarget(user);
  };

  const handleOpenResetModal = (user: AdminUser) => {
    resetPasswordMutation();
    setResetTarget(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowNewPassword(false);
    setResetSuccess(false);
  };

  const toggleDraftPerm = (key: keyof AdminUser['permissions']) => {
    setDraftPerms((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <DashboardLayout onSearch={handleSearch} searchPlaceholder="Search users...">
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-2 text-xs text-on-surface-variant mb-2">
            <button onClick={() => navigate('/admin')} className="hover:text-primary transition-colors">Admin</button>
            <span className="material-symbols-outlined text-sm">chevron_right</span>
            <span>All Users</span>
          </div>
          <div className={styles.titleRow}>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                <span className="material-symbols-outlined">manage_accounts</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight text-on-surface font-headline">All Users</h2>
                <p className="text-on-surface-variant text-sm">{total} {roleFilter || accountFilter || parentFilter ? 'matching' : 'registered'} users</p>
              </div>
            </div>
            <button
              className={styles.recalcAllBtn}
              disabled={recalcStarting}
              onClick={() => {
                if (window.confirm('Read the real size of every project owner\'s folders from S3 and save it to the storage records, and recount each project\'s Storage Used from its files? Nothing in S3 changes.')) {
                  void recalculateStorage({ variables: {} });
                }
              }}
              title="Read every project owner's real storage from S3 and recount each project's Storage Used"
            >
              <span className="material-symbols-outlined text-sm">database</span>
              Recalculate storage for all
            </button>
          </div>
        </div>

        {recalcError && (
          <p className="text-xs text-error bg-error/10 px-3 py-2 rounded-lg" role="alert">
            Storage could not be recalculated: {recalcError.message}
          </p>
        )}
        <StorageRecalcPanel />

        <div className={styles.filters}>
          <label className={styles.filter}>
            <span>Role</span>
            <select aria-label="Role" value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}>
              <option value="">All roles</option>
              <option value="superadmin">Super admins</option>
              <option value="admin">Admins</option>
              <option value="user">Users</option>
            </select>
          </label>
          <label className={styles.filter}>
            <span>Account</span>
            <select aria-label="Account" value={parentFilter ? '' : accountFilter} disabled={!!parentFilter} onChange={(e) => { setAccountFilter(e.target.value); setPage(1); }}>
              <option value="">Main users and subusers</option>
              <option value="main">Main users</option>
              <option value="subuser">Subusers</option>
            </select>
          </label>
          {parentFilter && (
            <span className={styles.parentChip}>
              Subusers of {parentFilter.firstName} {parentFilter.lastName}
              <button type="button" aria-label="Show every user" onClick={() => { setParentFilter(null); setPage(1); }}>
                <span className="material-symbols-outlined text-sm" aria-hidden="true">close</span>
              </button>
            </span>
          )}
        </div>

        {toggleError && !deactivateTarget && (
          <p className="text-xs text-error bg-error/10 px-3 py-2 rounded-lg">{toggleError.message}</p>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-on-surface-variant">
            <span className="material-symbols-outlined animate-spin mr-2">refresh</span>
            Loading users...
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <div className="w-16 h-16 bg-error/10 text-error rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="material-symbols-outlined text-3xl">error</span>
            </div>
            <h3 className="text-lg font-bold text-on-surface mb-2">Could not load users</h3>
            <p className="text-sm text-on-surface-variant">{error.message}</p>
          </div>
        ) : users.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="material-symbols-outlined text-3xl">group</span>
            </div>
            <h3 className="text-lg font-bold text-on-surface mb-2">No users found</h3>
            <p className="text-sm text-on-surface-variant">No users match your search criteria.</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl overflow-hidden">
              <div className={styles.tableWrap}>
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-surface-container-low">
                    <th className="table-header">User</th>
                    <th className="table-header">Company</th>
                    <th className="table-header">Role</th>
                    <th className="table-header">Account</th>
                    <th className="table-header">Plan</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">Last Login</th>
                    <th className="table-header">Joined</th>
                    <th className="table-header text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/10">
                  {users.map((user) => (
                    <tr key={user.id} className={`table-row group ${!user.isActive ? 'opacity-50' : ''}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary-container flex items-center justify-center text-white text-xs font-bold shrink-0">
                            {user.firstName[0]}{user.lastName[0]}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-on-surface">{user.firstName} {user.lastName}</p>
                            <p className="text-xs text-on-surface-variant">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-on-surface-variant">{user.companyName}</td>
                      <td className="px-6 py-4">
                        <span className={ROLE_COLORS[user.role] || 'badge-green'}>{roleLabel(user.role)}</span>
                        {user.role === 'admin' && (
                          <p className={styles.modules} title={MODULE_OPTIONS.filter((m) => user.adminModules.includes(m.value)).map((m) => m.label).join(', ')}>
                            {user.adminModules.length === 0 ? 'No modules' : `${user.adminModules.length} module${user.adminModules.length === 1 ? '' : 's'}`}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 text-xs">
                        {user.isSubuser ? (
                          <>
                            <p className="text-on-surface">Subuser of {user.parentUser?.email ?? 'a removed account'}</p>
                            <p className="text-on-surface-variant">{SUBUSER_STATUS[user.subuserStatus ?? ''] ?? ''}</p>
                          </>
                        ) : (
                          <>
                            <p className="text-on-surface">Main user</p>
                            {user.subuserCount > 0 && <p className="text-on-surface-variant">{user.subuserCount} subuser{user.subuserCount === 1 ? '' : 's'}</p>}
                          </>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {user.isSubuser ? (
                          <span className="text-[10px] font-bold text-on-surface-variant bg-surface-container px-2 py-1 rounded-full" title="A subuser uses its main user's plan">
                            Main user's plan
                          </span>
                        ) : user.userPlan ? (
                          <span className="text-[10px] font-bold text-yellow-700 bg-yellow-50 px-2 py-1 rounded-full">
                            {user.userPlan.name}
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold text-on-surface-variant bg-surface-container px-2 py-1 rounded-full">
                            No plan
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={user.isActive ? 'badge-green' : 'text-[10px] font-bold text-red-600 bg-red-50 px-2 py-1 rounded-full'}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs text-on-surface-variant">{formatDate(user.lastLoginAt)}</td>
                      <td className="px-6 py-4 text-xs text-on-surface-variant">{formatDate(user.createdAt)}</td>
                      <td className="px-6 py-4 text-right">
                        <ActionsMenu
                          label={`Actions for ${user.firstName} ${user.lastName}`}
                          actions={[
                            {
                              label: 'View activity logs', icon: 'receipt_long',
                              onSelect: () => navigate(`/activity?userId=${user.id}`, { state: { userName: `${user.firstName} ${user.lastName}` } }),
                            },
                            {
                              label: 'Recalculate storage', icon: 'database', disabled: recalcStarting,
                              hint: 'Read this user\'s real storage from S3 and recount their projects\' Storage Used',
                              onSelect: () => void recalculateStorage({ variables: { userId: user.id } }),
                            },
                            !user.isSubuser && user.subuserCount > 0 && {
                              label: 'Show subusers', icon: 'group',
                              onSelect: () => { setParentFilter(user); setPage(1); },
                            },
                            mayManage(user) && !user.isSubuser && { label: 'Assign plan', icon: 'workspace_premium', onSelect: () => handleOpenPlanModal(user) },
                            mayManage(user) && !user.isSubuser && { label: 'Edit permissions', icon: 'key', onSelect: () => handleOpenPermModal(user) },
                            isSuperAdmin && user.id !== currentUser?.id && { label: 'Change role', icon: 'manage_accounts', onSelect: () => handleOpenRoleModal(user) },
                            mayManage(user) && { label: 'Reset password', icon: 'lock_reset', onSelect: () => handleOpenResetModal(user) },
                            mayManage(user) && {
                              label: user.isActive ? 'Deactivate user' : 'Activate user',
                              icon: user.isActive ? 'person_off' : 'person_check',
                              danger: user.isActive,
                              onSelect: () => handleToggleActive(user),
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>

            {totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-outline-variant disabled:opacity-40 hover:bg-surface-container transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-on-surface-variant">
                  Page {page} of {totalPages}
                </span>
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-3 py-1.5 text-sm rounded-lg border border-outline-variant disabled:opacity-40 hover:bg-surface-container transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <Modal
        isOpen={!!roleTarget}
        onClose={() => setRoleTarget(null)}
        title="Change User Role"
        footer={
          <>
            <button
              onClick={() => setRoleTarget(null)}
              className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => setUserRole({
                variables: { userId: roleTarget!.id, role: selectedRole, adminModules: selectedRole === 'admin' ? selectedModules : [] },
              })}
              disabled={savingRole}
              className="px-5 py-2 rounded-lg text-sm font-medium bg-primary text-on-primary hover:opacity-90 disabled:opacity-50"
            >
              {savingRole ? 'Saving…' : 'Save Role'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-on-surface-variant">
            Change role for <strong>{roleTarget?.firstName} {roleTarget?.lastName}</strong>
          </p>
          {roleError && (
            <p className="text-xs text-error bg-error/10 px-3 py-2 rounded-lg">{roleError.message}</p>
          )}
          <div className="space-y-2">
            {ROLES.map((role) => (
              <label key={role.value} className="flex items-center gap-3 p-3 rounded-lg border border-outline-variant cursor-pointer hover:bg-surface-container transition-colors">
                <input
                  type="radio"
                  name="role"
                  value={role.value}
                  checked={selectedRole === role.value}
                  onChange={() => setSelectedRole(role.value)}
                  className="accent-primary"
                />
                <div>
                  <p className="text-sm font-medium">{roleLabel(role.value)}</p>
                  <p className="text-xs text-on-surface-variant">{role.description}</p>
                </div>
              </label>
            ))}
          </div>
          {selectedRole === 'admin' && (
            <div className={styles.modulePicker}>
              <MultiSelectDropdown
                label="Admin modules"
                options={MODULE_OPTIONS}
                selected={selectedModules}
                onChange={setSelectedModules}
                placeholder="No modules — only the Admin Dashboard"
              />
              <p className="text-xs text-on-surface-variant">
                The admin sees only these pages, in the sidebar and on /admin. All Projects also lets them open every project.
              </p>
            </div>
          )}
        </div>
      </Modal>
      <Modal
        isOpen={!!permTarget}
        onClose={() => setPermTarget(null)}
        title="Edit Permissions"
        footer={
          <>
            <button
              onClick={() => setPermTarget(null)}
              className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => updatePermissions({ variables: { userId: permTarget!.id, permissions: draftPerms } })}
              disabled={savingPerms}
              className="px-5 py-2 rounded-lg text-sm font-medium bg-primary text-on-primary hover:opacity-90 disabled:opacity-50"
            >
              {savingPerms ? 'Saving…' : 'Save Permissions'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-on-surface-variant">
            Permissions for <strong>{permTarget?.firstName} {permTarget?.lastName}</strong>
          </p>
          {permsError && (
            <p className="text-xs text-error bg-error/10 px-3 py-2 rounded-lg">{permsError.message}</p>
          )}
          <div className="space-y-2">
            {PERMISSIONS_META.map(({ key, label, description }) => (
              <label
                key={key}
                className="flex items-center justify-between gap-4 p-3 rounded-lg border border-outline-variant cursor-pointer hover:bg-surface-container transition-colors"
              >
                <div>
                  <p className="text-sm font-medium">{label}</p>
                  <p className="text-xs text-on-surface-variant">{description}</p>
                </div>
                <input
                  type="checkbox"
                  checked={!!draftPerms[key]}
                  onChange={() => toggleDraftPerm(key)}
                  className="accent-primary w-4 h-4 flex-shrink-0"
                />
              </label>
            ))}
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={!!planTarget}
        onClose={() => setPlanTarget(null)}
        title="Assign User Plan"
        footer={
          <>
            <button
              onClick={() => setPlanTarget(null)}
              className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => assignUserPlan({ variables: { userId: planTarget!.id, planId: selectedPlanId || null } })}
              disabled={savingPlan}
              className="px-5 py-2 rounded-lg text-sm font-medium bg-primary text-on-primary hover:opacity-90 disabled:opacity-50"
            >
              {savingPlan ? 'Saving…' : 'Save Plan'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-on-surface-variant">
            Assign a plan to <strong>{planTarget?.firstName} {planTarget?.lastName}</strong>.
            If no plan is selected, the user has no storage or download restrictions.
          </p>
          {planError && (
            <p className="text-xs text-error bg-error/10 px-3 py-2 rounded-lg">{planError.message}</p>
          )}
          <div className="space-y-2">
            <label className="flex items-center gap-3 p-3 rounded-lg border border-outline-variant cursor-pointer hover:bg-surface-container transition-colors">
              <input
                type="radio"
                name="plan"
                value=""
                checked={selectedPlanId === ''}
                onChange={() => setSelectedPlanId('')}
                className="accent-primary"
              />
              <div>
                <p className="text-sm font-medium">No plan <span className="text-xs text-on-surface-variant">(unrestricted)</span></p>
              </div>
            </label>
            {userPlanOptions.map((plan) => (
              <label key={plan.id} className="flex items-center gap-3 p-3 rounded-lg border border-outline-variant cursor-pointer hover:bg-surface-container transition-colors">
                <input
                  type="radio"
                  name="plan"
                  value={plan.id}
                  checked={selectedPlanId === plan.id}
                  onChange={() => setSelectedPlanId(plan.id)}
                  className="accent-primary"
                />
                <p className="text-sm font-medium">{plan.name}</p>
              </label>
            ))}
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={!!deactivateTarget}
        onClose={() => setDeactivateTarget(null)}
        title="Deactivate User"
        footer={
          <>
            <button
              onClick={() => setDeactivateTarget(null)}
              className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => toggleUserActive({ variables: { userId: deactivateTarget!.id } })}
              disabled={toggling}
              className="px-5 py-2 rounded-lg text-sm font-medium bg-error text-on-error hover:opacity-90 disabled:opacity-50"
            >
              {toggling ? 'Deactivating…' : 'Deactivate'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-on-surface-variant">
            Deactivate <strong>{deactivateTarget?.firstName} {deactivateTarget?.lastName}</strong> ({deactivateTarget?.email})?
            They will no longer be able to sign in until an admin activates them again.
          </p>
          {toggleError && (
            <p className="text-xs text-error bg-error/10 px-3 py-2 rounded-lg">{toggleError.message}</p>
          )}
        </div>
      </Modal>
      <Modal
        isOpen={!!resetTarget}
        onClose={() => { setResetTarget(null); setResetSuccess(false); }}
        title="Reset Password"
        footer={
          resetSuccess ? (
            <button
              onClick={() => { setResetTarget(null); setResetSuccess(false); }}
              className="px-5 py-2 rounded-lg text-sm font-medium bg-primary text-on-primary hover:opacity-90"
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={() => setResetTarget(null)}
                className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => resetUserPassword({ variables: { userId: resetTarget!.id, newPassword } })}
                disabled={resetting || newPassword.length < 8 || newPassword !== confirmPassword}
                className="px-5 py-2 rounded-lg text-sm font-medium bg-orange-600 text-white hover:opacity-90 disabled:opacity-50"
              >
                {resetting ? 'Resetting…' : 'Reset Password'}
              </button>
            </>
          )
        }
      >
        {resetSuccess ? (
          <div className="flex flex-col items-center gap-3 py-4">
            <div className="w-12 h-12 bg-green-50 text-green-600 rounded-full flex items-center justify-center">
              <span className="material-symbols-outlined text-2xl">check_circle</span>
            </div>
            <p className="text-sm text-on-surface text-center">
              Password for <strong>{resetTarget?.firstName} {resetTarget?.lastName}</strong> has been reset successfully.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-on-surface-variant">
              Set a new password for <strong>{resetTarget?.firstName} {resetTarget?.lastName}</strong> ({resetTarget?.email}).
            </p>
            {resetError && (
              <p className="text-xs text-error bg-error/10 px-3 py-2 rounded-lg">{resetError.message}</p>
            )}
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-on-surface-variant mb-1">New Password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                    className="w-full px-3 py-2 pr-10 text-sm border border-outline-variant rounded-lg focus:outline-none focus:border-primary"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface"
                  >
                    <span className="material-symbols-outlined text-lg">{showNewPassword ? 'visibility_off' : 'visibility'}</span>
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-on-surface-variant mb-1">Confirm Password</label>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat new password"
                  className="w-full px-3 py-2 text-sm border border-outline-variant rounded-lg focus:outline-none focus:border-primary"
                />
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-error mt-1">Passwords do not match</p>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
};

export default AdminUsers;
